import {
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { toJS } from "mobx";
import {
  baseDashboardQueryKey,
  baseQualityEventsQueryKey,
  baseUserTodosQueryKey,
  baseUserTodosSummaryQueryKey,
  getOrgTasksQueryKey,
  getQualityEventRecordQueryKey,
} from "src/config/request-keys";
import { getFile, uploadFile } from "src/services/backend";
import {
  QualityEventRecordUpdate,
  saveQualityEventRecord,
} from "src/services/quality-events";
import { UploadFileResponseSchema } from "src/stores/models";
import { QualityEventDraft, qualityEventRows } from "src/types/quality-events";
import { downloadFile } from "src/utils/files";

export const usePrepareQualityEventDraft = ({ orgId }: { orgId: string }) =>
  useMutation({
    mutationFn: async (draft: QualityEventDraft) => {
      const answers = structuredClone(toJS(draft.answers));
      for (const [fieldPath, files] of Object.entries(draft.files)) {
        if (!files.length) continue;

        // Paths are fieldKey or repeatableFieldKey:rowId:nestedFieldKey.
        const [fieldKey, rowId, nestedFieldKey] = fieldPath.split(":");
        const targetAnswers =
          rowId === undefined
            ? answers
            : qualityEventRows(answers[fieldKey]).find(
                (row) => row.id === rowId
              )?.answers;
        if (!targetAnswers)
          throw new Error(
            "Some evidence belongs to a removed action. Review the unattached evidence before saving."
          );

        const uploadedFileIds = await Promise.all(
          files.map(
            async (file) =>
              UploadFileResponseSchema.validateSync(
                (await uploadFile({ orgId, file })).data
              ).id
          )
        );
        const answerKey = nestedFieldKey ?? fieldKey;
        const existing = targetAnswers[answerKey];
        targetAnswers[answerKey] = [
          ...(Array.isArray(existing)
            ? existing.filter((id): id is string => typeof id === "string")
            : []),
          ...uploadedFileIds,
        ];
      }

      return {
        title: draft.title,
        answers,
      };
    },
  });

export const invalidateQualityEvents = (
  queryClient: QueryClient,
  orgId: string
) =>
  Promise.all(
    [
      [...baseQualityEventsQueryKey, orgId],
      getOrgTasksQueryKey(orgId, "all"),
      getOrgTasksQueryKey(orgId, "mine"),
      [...baseUserTodosQueryKey, orgId],
      [...baseUserTodosSummaryQueryKey, orgId],
      [...baseDashboardQueryKey, orgId],
    ].map((queryKey) => queryClient.invalidateQueries({ queryKey }))
  );

export const useSaveQualityEventRecord = ({ orgId }: { orgId: string }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: {
      recordId: string;
      body: QualityEventRecordUpdate;
    }) => saveQualityEventRecord(orgId, request),
    onError: (error, { recordId }) => {
      if (isAxiosError(error) && error.response?.status === 409)
        return queryClient.invalidateQueries({
          queryKey: getQualityEventRecordQueryKey(orgId, recordId),
        });
    },
    onSuccess: async (record) => {
      queryClient.setQueryData(
        getQualityEventRecordQueryKey(orgId, record.id),
        record
      );
      await invalidateQualityEvents(queryClient, orgId);
    },
  });
};

export const useDownloadQualityEventFile = ({ orgId }: { orgId: string }) =>
  useMutation({
    mutationFn: async ({
      fileId,
      filename,
    }: {
      fileId: string;
      filename: string;
    }) => {
      const { data } = await getFile({ orgId, fileId });
      await downloadFile(data, filename);
    },
  });
